// Manual "Precios, comprobantes y cierre de caja" (2026-10-23). Se muestra en Ayuda y en
// /ayuda/imprimir?manual=caja (versión sin menú de donde sale el PDF
// public/ayuda/Manual_Precios_Comprobantes_Cierre.pdf). Imágenes en public/ayuda/caja/.
const Paso = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <li className="flex gap-3 items-start">
    <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">{n}</span>
    <div className="pt-0.5">{children}</div>
  </li>
);
const Imagen = ({ src, alt, ancho = 'max-w-2xl' }: { src: string; alt: string; ancho?: string }) => (
  <img src={`/ayuda/caja/${src}`} alt={alt} className={`${ancho} w-full border rounded-lg shadow-sm my-3 manual-imagen`} />
);
const Seccion = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <section className="mb-8 manual-seccion">
    <h2 className="text-xl font-bold text-blue-800 border-b-2 border-blue-200 pb-1 mb-3">{titulo}</h2>
    <div className="space-y-3 text-gray-800 leading-relaxed">{children}</div>
  </section>
);
const Aviso = ({ children, color = 'amber' }: { children: React.ReactNode; color?: 'amber' | 'red' | 'green' | 'blue' }) => {
  const c = { amber: 'bg-amber-50 border-amber-400', red: 'bg-red-50 border-red-400', green: 'bg-green-50 border-green-500', blue: 'bg-blue-50 border-blue-400' }[color];
  return <div className={`border-l-4 ${c} px-4 py-2 rounded`}>{children}</div>;
};

const ManualCaja = () => (
  <div className="manual">
    <div className="mb-8">
      <h1 className="text-3xl font-bold text-gray-900">Precios, comprobantes y cierre de caja</h1>
      <p className="text-gray-600">Sistema Rápido · Manual de uso · Actualizado el 23/10/2026</p>
    </div>

    <Seccion titulo="1. Lo nuevo en el sistema">
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Cada persona entra con su propio usuario.</b> Arriba a la derecha se ve quién tiene la sesión abierta.</li>
        <li><b>Cambiar el precio al vender</b> se puede, pero pide la <b>clave de administrador</b> y un motivo.</li>
        <li><b>Comprobantes RI y RE:</b> cada vez que entra o sale dinero, el sistema le pone un número.</li>
        <li><b>Cierre de caja diario:</b> al final del día se cuenta el efectivo de cada caja y se cierra.</li>
        <li><b>Auditoría:</b> todo lo que se hace queda registrado con el nombre de quien lo hizo.</li>
      </ul>
      <Imagen src="01_usuario_barra.png" alt="Usuario en la barra superior" ancho="max-w-md" />
      <Aviso color="blue">No compartan usuarios: si alguien usa la sesión de otro, en Auditoría saldrá a nombre de la persona equivocada.</Aviso>
    </Seccion>

    <Seccion titulo="2. Cambiar el precio de un producto al vender">
      <p>En <b>Ventas</b>, cada producto del carrito muestra su precio con un <b>lápiz</b> al lado.</p>
      <ol className="space-y-3">
        <Paso n={1}>Agrega el producto al carrito y toca el <b>precio con el lápiz</b>.</Paso>
        <Paso n={2}>Escribe el <b>nuevo precio</b>, el <b>motivo</b> (por ejemplo "cliente frecuente, precio acordado") y la <b>clave de administrador</b>.</Paso>
        <Paso n={3}>Toca <b>Aplicar precio</b>. En el carrito se ve el precio de lista tachado y el nuevo en azul.</Paso>
        <Paso n={4}>Procesa la venta como siempre.</Paso>
      </ol>
      <div className="flex flex-wrap gap-4 items-start fila-imagenes">
        <Imagen src="03_cambiar_precio.png" alt="Ventana Cambiar precio" ancho="max-w-xs" />
        <Imagen src="04_carrito_precio.png" alt="Carrito con el precio cambiado" ancho="max-w-xs" />
      </div>
      <ul className="list-disc pl-6 space-y-1">
        <li>El precio cambia <b>solo para esa venta</b>. El precio del producto en Stock no se modifica.</li>
        <li><b>Producto sin precio (S/ 0):</b> se escribe el precio sin clave y queda guardado para las próximas ventas.</li>
        <li><b>Descuento:</b> también pide la clave de administrador antes de procesar la venta.</li>
        <li>Funciona igual en <b>Venta Rápida</b> y en el <b>Libro de caja</b>.</li>
        <li>Los servicios manuales (mano de obra, lavado) tienen precio libre.</li>
      </ul>
      <Aviso>Cada cambio de precio y cada descuento queda en <b>Auditoría</b>: producto, precio de lista, precio cobrado, motivo y quién lo hizo.</Aviso>
    </Seccion>

    <Seccion titulo="3. Comprobantes RI y RE">
      <p>Son recibos internos numerados que respaldan cada movimiento de dinero:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><b className="text-green-700">RI = Recibo de Ingreso</b>: entra dinero (venta al contado, cobro de crédito, pago de mantenimiento…).</li>
        <li><b className="text-red-700">RE = Recibo de Egreso</b>: sale dinero (gasto, compra de repuestos, adelanto, planilla…).</li>
      </ul>
      <p>El número se pone <b>solo</b> al registrar el movimiento, en cualquier pantalla del sistema. Nadie lo escribe a mano.</p>
      <ol className="space-y-3">
        <Paso n={1}>Entra a <b>Comprobantes de caja</b> en el menú.</Paso>
        <Paso n={2}>Elige las fechas, la caja y el tipo (RI o RE). Puedes buscar por número o por concepto.</Paso>
        <Paso n={3}>Toca la <b>impresora</b> de la fila para imprimir el ticket. Se puede reimprimir las veces que haga falta.</Paso>
      </ol>
      <Imagen src="05_comprobantes.png" alt="Pantalla Comprobantes de caja" />
      <div className="flex flex-wrap gap-4 items-start fila-imagenes">
        <Imagen src="06_ticket.png" alt="Ticket de comprobante" ancho="max-w-[240px]" />
        <div className="flex-1 min-w-[240px] space-y-2">
          <p><b>El ticket tiene dos firmas:</b> quien entrega el dinero y quien lo recibe. Guarden los tickets firmados del día: son los que se revisan en el cierre.</p>
          <p><b>Comprobante ANULADO:</b> si un movimiento se anula (por ejemplo un pago de mantenimiento que no se cobró), su comprobante no se borra: queda tachado y marcado ANULADO, con quién y por qué. Así la numeración nunca tiene huecos.</p>
          <p><b>Anular una venta</b> no anula su RI: el sistema registra un <b>RE de devolución</b> por el dinero que sale.</p>
        </div>
      </div>
      <Aviso color="blue">Los movimientos registrados antes del 23/10/2026 no tienen número (aparecen con "—").</Aviso>
    </Seccion>

    <Seccion titulo="4. Cierre de caja diario">
      <p>Cada caja se cierra <b>todos los días</b>, por separado: <b>Caja Repuestos (ventas)</b>, <b>Caja Servicios</b>, <b>Caja Mantenimiento</b> y <b>Caja Chica (gastos y compras)</b>.</p>
      <ol className="space-y-3">
        <Paso n={1}>Entra a <b>Cierre de caja</b>, elige el día (por defecto hoy) y la caja.</Paso>
        <Paso n={2}>Revisa los <b>movimientos</b>: cada uno con su RI o RE, la forma de pago y quién lo registró. Compáralos con los tickets firmados.</Paso>
        <Paso n={3}>Mira el resumen: <b>Apertura + Ingresos en efectivo − Egresos en efectivo = Debe haber</b>.</Paso>
        <Paso n={4}>Cuenta el dinero y escribe <b>cuántos billetes y monedas</b> hay de cada tipo. El total se suma solo (o escríbelo directo en "Efectivo contado").</Paso>
        <Paso n={5}>El sistema dice si <b>cuadra exacto</b>, si <b>falta</b> o si <b>sobra</b>. Si hay diferencia, explícala en <b>Observaciones</b> (es obligatorio).</Paso>
        <Paso n={6}>Si hubo Yape, revisa en el celular que sumen lo mismo y marca la casilla.</Paso>
        <Paso n={7}>Toca <b>Cerrar caja del día</b> y confirma. Se descarga el <b>PDF del cierre</b>: imprímelo y fírmenlo cajero y dueño.</Paso>
      </ol>
      <Imagen src="10_cierre_cuadra.png" alt="Cierre de caja con el conteo" />
      <Imagen src="11_cierre_cerrado.png" alt="Caja cerrada y cierres anteriores" />
      <Imagen src="15_pdf_cierre-1.png" alt="PDF del cierre para firmar" ancho="max-w-md" />
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Apertura</b> = el efectivo que se contó en el cierre anterior de esa caja.</li>
        <li>El <b>Yape</b> se muestra aparte: ese dinero no está en la caja física.</li>
        <li>Si un día no se cerró, el cierre siguiente incluye también los movimientos de los días sin cerrar (el sistema lo avisa).</li>
        <li>En <b>Cierres anteriores</b> está el historial con sus diferencias y el PDF de cada uno.</li>
      </ul>
      <Aviso>
        <b>Primer cierre de cada caja = arqueo inicial.</b> La primera vez, la apertura es el efectivo que calcula el sistema. Cuenten lo que realmente hay:
        si no coincide, la diferencia queda documentada en ese cierre y <b>desde el día siguiente se parte del dinero contado</b>.
      </Aviso>
      <Imagen src="07_arqueo_inicial.png" alt="Aviso de arqueo inicial" />
    </Seccion>

    <Seccion titulo="5. Después del cierre: el día queda bloqueado">
      <p>Un día cerrado ya <b>no se puede modificar</b> en esa caja: no se pueden registrar, editar ni anular movimientos. Si alguien lo intenta, el sistema avisa:</p>
      <Imagen src="12_caja_cerrada_aviso.png" alt="Aviso de caja cerrada" />
      <p><b>Para corregir algo de un día cerrado</b> (por ejemplo, una venta que faltó registrar):</p>
      <ol className="space-y-3">
        <Paso n={1}>En <b>Cierre de caja</b>, en el cierre de ese día, toca <b>Reabrir</b>.</Paso>
        <Paso n={2}>Escribe el <b>motivo</b> y la <b>clave de administrador</b> (un dueño).</Paso>
        <Paso n={3}>Haz la corrección y <b>vuelve a cerrar</b> la caja de ese día.</Paso>
      </ol>
      <Imagen src="13_reabrir.png" alt="Reabrir un cierre" ancho="max-w-xs" />
      <Aviso color="red">El cierre reabierto no se borra: queda en el historial como "Reabierto por …" con el motivo, y en Auditoría.</Aviso>
    </Seccion>

    <Seccion titulo="6. Auditoría: quién hizo qué">
      <p>En <b>Auditoría</b> se ve todo lo que pasó en el sistema, con fecha, hora y <b>usuario</b>: ventas, pagos, gastos, cambios de precio, anulaciones, cierres, reaperturas, ingresos al sistema y hasta los intentos con clave equivocada.</p>
      <Imagen src="14_auditoria.png" alt="Auditoría con la columna Usuario" />
    </Seccion>

    <Seccion titulo="7. Preguntas frecuentes">
      <p><b>¿Puedo cerrar la caja de un día que ya pasó?</b> Sí, elige el día en "Día". Pero no se puede cerrar un día anterior a otro que ya está cerrado.</p>
      <p><b>Me equivoqué en el conteo y ya cerré.</b> Un dueño reabre el cierre con la clave y se vuelve a cerrar con el conteo correcto.</p>
      <p><b>Un pago se registró pero el dinero no llegó.</b> Se anula desde su pantalla (con clave y motivo). Su comprobante queda ANULADO y el monto sale de la caja.</p>
      <p><b>¿Por qué el pago dice "efectivo" si fue Yape?</b> Cuando una pantalla no pregunta la forma de pago (planillas, adelantos, gastos), el sistema lo registra como efectivo. Si fue Yape, avisen a un dueño para corregirlo antes del cierre.</p>
      <p><b>Olvidé la clave de administrador o mi contraseña.</b> Pídanla a un dueño o a TLI – Tu Lugar Inteligente (931 398 916).</p>
    </Seccion>
  </div>
);

export default ManualCaja;
